"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AlignmentGeometryFactory = void 0;
const tslib_1 = require("tslib");
const homography_1 = require("../homography");
const vector_1 = require("./../vector");
const Project = tslib_1.__importStar(require("./coordinate.projection"));
const geometry_3d_1 = require("./geometry.3d");
const GeoUtil = tslib_1.__importStar(require("./geometry.util"));
let POINTS = GeoUtil.POINTS;
let PLANE_ORIGIN_POINT = POINTS.TOP_LEFT;
class AlignmentGeometryFactory {
    create(ctx) {
        let geoCtx = {
            planeCenter_MRW: null,
            planeNormal_MRW: null,
            planeAxis_MP: null,
            planeOrigin_MRW: null,
            focalLength: null,
            homography: null,
            calibration: null,
            ctx,
            createdBy: geometry_3d_1.Geometry3DCreationMethod.ALIGNMENT_GRID,
        };
        if (ctx.calibration) {
            geoCtx.focalLength = ctx.calibration.focal;
            geoCtx.calibration = ctx.calibration;
        }
        else {
            geoCtx.focalLength = ctx.focalLength || GeoUtil.calculateFocalLength(ctx.resolution, ctx.viewAngle);
        }
        geoCtx.planeCenter_MRW = new vector_1.Vector(0, 0, -ctx.distance);
        geoCtx.planeNormal_MRW = AlignmentGeometryFactory.calculatePlaneNormal(ctx, geoCtx);
        geoCtx.planeOrigin_MRW = AlignmentGeometryFactory.calculatePlaneOrigin(ctx, geoCtx);
        geoCtx.planeAxis_MP = AlignmentGeometryFactory.calculateAxisVectors(ctx, geoCtx);
        geoCtx.homography = AlignmentGeometryFactory.calculateHomography(ctx, geoCtx);
        return new geometry_3d_1.Geometry3D(geoCtx);
    }
    static calculatePlaneOrigin(ctx, geoCtx) {
        let alignment_CI = ctx.alignment;
        let origin_CI = alignment_CI[PLANE_ORIGIN_POINT];
        return Project.CalibratedToWorld(geoCtx.focalLength, geoCtx.planeCenter_MRW, geoCtx.planeNormal_MRW, ctx.resolution, origin_CI);
    }
    static calculateHomography(ctx, geoCtx) {
        let alignment_CI = ctx.alignment;
        let ProjectCalibratedToWorld = Project.CalibratedToWorld.bind(null, geoCtx.focalLength, geoCtx.planeCenter_MRW, geoCtx.planeNormal_MRW, ctx.resolution);
        let ProjectCalibratedToImage = Project.CalibratedToImage.bind(null, ctx.resolution);
        let alignment_MRW = alignment_CI.map(ProjectCalibratedToWorld);
        let dimensions = GeoUtil.estimateDimensions(alignment_MRW);
        var alignment_MP = AlignmentGeometryFactory.CalculateAlignmentPlaneQuad(dimensions);
        var alignment_IP = alignment_CI.map(ProjectCalibratedToImage);
        return homography_1.Homography.create(alignment_IP, alignment_MP);
    }
    static calculatePlaneNormal(ctx, geoCtx) {
        let alignment_CI = ctx.alignment;
        let ProjectCalibratedToImage = Project.CalibratedToImage.bind(null, ctx.resolution);
        let alignment_IP = alignment_CI.map(ProjectCalibratedToImage);
        let horIntersection = GeoUtil.lineIntersectionPoint({
            start: alignment_IP[POINTS.BOTTOM_LEFT],
            end: alignment_IP[POINTS.BOTTOM_RIGHT],
        }, {
            start: alignment_IP[POINTS.TOP_LEFT],
            end: alignment_IP[POINTS.TOP_RIGHT],
        });
        let vertIntersection = GeoUtil.lineIntersectionPoint({
            start: alignment_IP[POINTS.BOTTOM_LEFT],
            end: alignment_IP[POINTS.TOP_LEFT],
        }, {
            start: alignment_IP[POINTS.BOTTOM_RIGHT],
            end: alignment_IP[POINTS.TOP_RIGHT],
        });
        let horVanish;
        let vertVanish;
        //This is a special case where the horizontal lines are parallel (they do not intersect at any point)
        if (horIntersection === null) {
            horVanish = new vector_1.Vector(1, 0, 0);
        }
        else {
            horVanish = new vector_1.Vector(horIntersection.x, horIntersection.y, geoCtx.focalLength);
        }
        //This is a special case where the vertical lines are parallel (they do not intersect at any point)
        if (vertIntersection === null) {
            vertVanish = new vector_1.Vector(0, 1, 0);
        }
        else {
            vertVanish = new vector_1.Vector(vertIntersection.x, vertIntersection.y, geoCtx.focalLength);
        }
        let planeNormal_MRW = horVanish.cross(vertVanish);
        let magnitude = planeNormal_MRW.norm();
        //Don't divide by zero please (It just equals -1/12 anyway)
        if (magnitude === 0.0) {
            // We want to return something to make sure we don't implode later calculations
            planeNormal_MRW = new vector_1.Vector(0, 0, 1);
        }
        else {
            planeNormal_MRW = planeNormal_MRW.multiply(1.0 / magnitude);
            // If the plane normal is facing towards us, flip it to ensure that extrusion works as expected
            if (planeNormal_MRW.z < 0.0) {
                planeNormal_MRW = planeNormal_MRW.multiply(-1.0);
            }
        }
        return planeNormal_MRW;
    }
    static calculateAxisVectors(ctx, geoCtx) {
        let alignment_CI = ctx.alignment;
        let planeAxis_MP = { x: null, y: null };
        let ProjectCalibratedToWorld = Project.CalibratedToWorld.bind(null, geoCtx.focalLength, geoCtx.planeCenter_MRW, geoCtx.planeNormal_MRW, ctx.resolution);
        let alignment_MRW = alignment_CI.map(ProjectCalibratedToWorld);
        planeAxis_MP.y = alignment_MRW[POINTS.BOTTOM_LEFT].subtract(alignment_MRW[POINTS.TOP_LEFT]);
        let yMagnitude = planeAxis_MP.y.norm();
        planeAxis_MP.x = alignment_MRW[POINTS.BOTTOM_RIGHT].subtract(alignment_MRW[POINTS.BOTTOM_LEFT]);
        let xMagnitude = planeAxis_MP.x.norm();
        // Protect our selves against dividing by zero
        if (xMagnitude !== 0.0 && yMagnitude !== 0.0) {
            planeAxis_MP.x = planeAxis_MP.x.multiply(1.0 / xMagnitude);
            planeAxis_MP.y = planeAxis_MP.y.multiply(1.0 / yMagnitude);
        }
        return planeAxis_MP;
    }
    static CalculateAlignmentPlaneQuad(points_RW) {
        var alignment_MP = [];
        //Note in Measurement Plane coordinate (_mpc) space, the Y Axis points down positive. [So that it matches with Raw image]
        alignment_MP[POINTS.TOP_LEFT] = new vector_1.Vector(0, 0);
        alignment_MP[POINTS.TOP_RIGHT] = new vector_1.Vector(points_RW.x, 0);
        alignment_MP[POINTS.BOTTOM_RIGHT] = new vector_1.Vector(points_RW.x, points_RW.y);
        alignment_MP[POINTS.BOTTOM_LEFT] = new vector_1.Vector(0, points_RW.y);
        return alignment_MP;
    }
}
exports.AlignmentGeometryFactory = AlignmentGeometryFactory;
//# sourceMappingURL=geometry.factory.alignment.js.map